import { socialImageMeta } from "@/lib/social-sharing";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import {
  hreflangLinks,
  trPersonSchema,
  trPublisherSchema,
  TR_PERSON_ID,
  TR_PUBLISHER_ID,
} from "@/lib/seo";
import { pageTitle } from "@/lib/brand";
import { getCop31Note, cop31NotePath } from "@/lib/cop31-notes";
import { cop31NoteBodies } from "@/lib/cop31-note-bodies";
import { PageIntro, PageSection, Prose, MoreLink } from "@/components/site/page";

const ShareButtons = lazy(() =>
  import("@/components/ShareButtons").then((m) => ({ default: m.ShareButtons })),
);


export const Route = createFileRoute("/cop31_/notlar/$slug")({
  loader: ({ params }) => {
    const note = getCop31Note(params.slug);
    if (!note || note.locale === "en") throw notFound();
    if (params.slug !== note.slug) {
      throw redirect({ to: "/cop31/notlar/$slug", params: { slug: note.slug }, statusCode: 301 });
    }
    return { note, body: cop31NoteBodies[note.slug]?.tr ?? note.summaryTr };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [{ title: pageTitle("COP31 notu bulunamadı", "tr") }, { name: "robots", content: "noindex" }],
      };
    }
    const { note } = loaderData;
    const url = `https://akillisurdurulebilirlik.com/cop31/notlar/${params.slug}`;
    return {
      meta: [
        { title: pageTitle(`${note.titleTr} — COP31 notu`, "tr") },
        { name: "description", content: note.summaryTr },
        { property: "og:title", content: `${note.titleTr} — COP31 notu` },
        { property: "og:description", content: note.summaryTr },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:locale", content: "tr_TR" },
        // Every TR note has an English page at /cop31-guide/notes/<slug>.
        { property: "og:locale:alternate", content: "en_US" },
        { property: "article:published_time", content: `${note.iso}T00:00:00Z` },
        { property: "article:section", content: "İklim" },
        { property: "article:tag", content: "COP31" },
        { name: "twitter:title", content: `${note.titleTr} — COP31 notu` },
        { name: "twitter:description", content: note.summaryTr },
        ...socialImageMeta(),
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(cop31NotePath(note.slug, "tr"), cop31NotePath(note.slug, "en")),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(trPersonSchema()) },
        { type: "application/ld+json", children: JSON.stringify(trPublisherSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AnalysisNewsArticle",
            headline: note.titleTr,
            description: note.summaryTr,
            datePublished: note.iso,
            inLanguage: "tr-TR",
            articleSection: "COP31 Analiz",
            mainEntityOfPage: url,
            dateModified: note.iso,
            author: { "@id": TR_PERSON_ID },
            publisher: { "@id": TR_PUBLISHER_ID },
            isPartOf: {
              "@type": "WebPage",
              name: "COP31 Antalya 2026 — Rehber ve Analiz",
              url: "https://akillisurdurulebilirlik.com/cop31",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
              { "@type": "ListItem", position: 2, name: "COP31 Rehberi", item: "https://akillisurdurulebilirlik.com/cop31" },
              { "@type": "ListItem", position: 3, name: note.titleTr, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: NotePage,
});

function NotePage() {
  const { note, body } = Route.useLoaderData();
  return (
    <>
      <PageIntro
        crumbs={[{ label: "COP31", to: "/cop31" }, { label: note.titleTr }]}
        kicker={note.dateTr}
        dot="bg-theme-planet"
        title={note.titleTr}
        lede={note.summaryTr}
      />

      <PageSection>
        <Prose className="max-w-[72ch]">
          {body.split(/\n\n+/).map((paragraph) => (
            <p key={paragraph.slice(0, 80)}>{paragraph}</p>
          ))}
        </Prose>

        <Suspense fallback={null}>
          <ShareButtons
            path={`/cop31/notlar/${note.slug}`}
            title={note.titleTr}
            excerpt={note.summaryTr}
            category="COP31 notu"
            lang="tr"
            size="md"
            className="mt-10 border-t border-ink/10 pt-6"
          />
        </Suspense>

        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-ink/10 pt-6">
          {note.url && <MoreLink href={note.url}>Yazının tamamı</MoreLink>}
          <MoreLink to="/cop31">Tüm COP31 notları</MoreLink>
        </div>
      </PageSection>
    </>
  );
}
